import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import Loader from '@/components/common/Loader/Loader';
import { AvailabilityTimezone } from '@/components/common/TimezoneSelect/timezoneSelect';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { CustomInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { Progress } from '@/components/ui/progress';
import { SelectComponent } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { showToast } from '@/components/ui/sonner';
import { CustomTextarea } from '@/components/ui/textarea';
import { TimePicker } from '@/components/ui/time-select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { GET_ALL_PROGRAM, GET_ALL_SPORTS, GET_ALL_TRAINING_TYPE } from '@/Graphql/queries';
import timezones from '@/lib/timezones.json';
import { handleError } from '@/lib/utils';
import { getRoutes } from '@/routes/routePaths';
import { Role } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { ArrowLeft, Calendar, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { GET_SESSION_BY_ID } from '../sessionDetails/GraphQl/queries';
import { CREATE_SESSION, UPDATE_SESSION } from '../sessions/GraphQl/mutation';
import { GET_SESSION_BALANCE } from '../sessions/GraphQl/queries';
import TrainerFeedbackForm from './components/trainerFeedbackForm';
import { GET_LOG_INFO } from './GraphQl/queries';

const feedbackSchema = z.object({
  expertise: z.string().min(1, 'Please rate the trainer&apos;s expertise'),
  communication: z.string().min(1, 'Please rate the trainer&apos;s communication effectiveness'),
  expectations: z.string().min(1, 'Please rate how well the session met your expectations'),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

const getNextNearest30Min = (dateTime: Date) => {
  const current = dayjs(dateTime);
  const minutes = current.minute();
  const next30Min = minutes < 30 ? 30 : 60;
  return current.minute(next30Min).second(0).millisecond(0).toDate();
};

const Feedback = () => {
  const { id } = useParams();
  const { data: trainingTypes, loading: trainingTypesLoading } = useQuery(GET_ALL_TRAINING_TYPE, {
    variables: { sort: { order: 1 }, limit: 30, offset: 0 },
  });

  const { data: programsData, loading: programsLoading } = useQuery(GET_ALL_PROGRAM, {
    variables: {
      limit: 10,
      offset: 0,
    },
  });

  const { data: sessionBalance } = useQuery(GET_SESSION_BALANCE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { data: sportsData, loading: sportsLoading } = useQuery(GET_ALL_SPORTS, {
    variables: {
      limit: 20,
      offset: 0,
      sort: { 'sportName': 1 },
    },
  });
  const { data: sessionData } = useQuery(GET_SESSION_BY_ID as any, {
    variables: { id: id || '' },
  });
  const [progress, setProgress] = useState(1);
  const [showNextSessionSchedule, setShowNextSessionSchedule] = useState(false);

  const { userDetails } = authState();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      communication: '',
      expectations: '',
      expertise: '',
    },
  });

  const { data, loading: sessionInfoLoading } = useQuery(GET_LOG_INFO, {
    variables: { id: id || '' },
    onCompleted(data) {
      if (!data.getLogInfo?.status) {
        navigate('/');
      }
    },
  });

  const [createSession, { loading: createSessionLoading }] = useMutation(CREATE_SESSION, {
    refetchQueries: ['GetAllSession', 'GetSessionBalance'],
    onCompleted: () => {
      showToast({ message: 'Session created successfully', variant: 'success' });
      navigate(getRoutes.Sessions, { replace: true });
    },
    onError: handleError,
  });

  const [updateSession, { loading }] = useMutation(UPDATE_SESSION, {
    onCompleted: () => {
      showToast({ message: 'Thank you for your feedback', variant: 'success' });
      if (getValues('expectations') === 'Below Expectations') {
        navigate(`/help-us-improve/${id}`, { replace: true });
      } else {
        navigate('/');
      }
    },
    onError: handleError,
    refetchQueries: ['GetAllSessionsCount', 'GetAllSession', 'GetSessionById'],
  });

  const onSubmit = (data: FeedbackFormData) => {
    if (id && !loading) {
      updateSession({
        variables: {
          data: {
            programId: sessionData?.getSessionById?.programId || '',
            _id: id,
            trainerExpertiseRating: data.expertise,
            trainerCommunicationEffectiveness: data.communication,
            sessionExpectationMet: data.expectations,
          },
        },
      });
    }
  };

  const redirectToHome = () => {
    navigate(getRoutes.Sessions, { replace: true });
  };

  // Use different form for trainer/coach
  const isTrainer =
    userDetails?.role === Role.Trainer ||
    userDetails?.role === Role.Coach ||
    userDetails?.role === Role.SuperAdmin;

  const [trainerFeedback, setTrainerFeedback] = useState({
    muscleGroups: [] as string[],
    performance: '',
    notes: '',
    exercise: '',
  });

  const handleSubmitCustomerFeedback = async () => {
    if (id && !loading) {
      if (showNextSessionSchedule) {
        try {
          const combinedDateTime = dayjs(nextSessionDate)
            .hour(dayjs(nextSessionTime).hour())
            .minute(dayjs(nextSessionTime).minute())
            .second(0)
            .millisecond(0);
          const adjustedDateTime = combinedDateTime.tz(selectedTimezone, true).toISOString();

          // Build session data with modal selections
          const sessionCreateData: any = {
            timeZone: selectedTimezone,
            sessionStartTime: dayjs(adjustedDateTime).toISOString(),
            sessionEndTime: dayjs(adjustedDateTime).add(1, 'hour').toISOString(),
            traineeId: sessionData?.getSessionById?.traineeId || '',
            traineeName: sessionData?.getSessionById?.traineeIdUser?.displayName || '',
            programId: modalSelectedProgramId || sessionData?.getSessionById?.programId,
          };

          // Add conditional fields based on selected program
          if (modalSelectedProgram === 'fitprime' && selectedTrainingType) {
            sessionCreateData.trainingTypeId = selectedTrainingType;
          }

          if (modalSelectedProgram === 'fitvarsity') {
            if (modalSelectedSport) {
              sessionCreateData.sportId = modalSelectedSport;
            }
            if (modalSelectedPosition) {
              sessionCreateData.position = modalSelectedPosition;
            }
          }

          await createSession({
            variables: {
              data: sessionCreateData,
            },
          });
        } catch (error) {
          return;
        }
      }
      updateSession({
        variables: {
          data: {
            _id: id,
            programId: sessionData?.getSessionById?.programId,
            clientPerformance: trainerFeedback?.performance,
            clientFeedback: trainerFeedback?.notes,
            exercisePerformed: trainerFeedback?.exercise,
            targetedMuscle: trainerFeedback?.muscleGroups,
          },
        },
      });
    }
  };

  // Helper to format enum-like strings: replace _ with space and capitalize first letter
  const formatEnumString = (str: string) => {
    if (!str) return '';
    const withSpaces = str.replace(/_/g, ' ').toLowerCase();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const timezonesList = timezones.map((tz) => ({
    // label: `(UTC${tz.offset >= 0 ? '+' : ''}${convertOffset(tz.offset)}) ${tz.value} (${tz.abbr})`,
    label: `${tz.text.split(') ')[0] + ')'} ${tz.value} (${tz.abbr})`,
    value: tz.utc,
  }));

  const [nextSessionDate, setNextSessionDate] = useState<Date | undefined>();
  const [isSessionTypeModalOpen, setIsSessionTypeModalOpen] = useState(false);
  const [selectedTrainingType, setSelectedTrainingType] = useState<string>('');
  const [nextSessionTime, setNextSessionTime] = useState<Date>(getNextNearest30Min(new Date()));
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    sessionData?.getSessionById?.timeZone || '',
  );
  const [dateError, setDateError] = useState<string | undefined>(undefined);

  // Modal state for program selection
  const [modalSelectedProgram, setModalSelectedProgram] = useState<string>('');
  const [modalSelectedProgramId, setModalSelectedProgramId] = useState<string>('');
  const [modalSelectedSport, setModalSelectedSport] = useState<string>('');
  const [modalSelectedPosition, setModalSelectedPosition] = useState<string>('');

  useEffect(() => {
    if (progress === 5) {
      setShowNextSessionSchedule(false);
      setSelectedTrainingType(sessionData?.getSessionById?.trainingType?._id || '');
      setSelectedTimezone(sessionData?.getSessionById?.timeZone || '');
      setDateError(undefined);
    }
  }, [progress]);

  // Initialize modal state when opening
  useEffect(() => {
    if (isSessionTypeModalOpen && programsData?.getAllProgram) {
      // Initialize with current session program
      const currentProgram = sessionData?.getSessionById?.programDetails?.programName?.toLowerCase() || '';
      const currentProgramData = programsData.getAllProgram.find(
        (p) => p?.programName?.toLowerCase() === currentProgram
      );

      if (currentProgramData) {
        setModalSelectedProgram(currentProgram);
        setModalSelectedProgramId(currentProgramData._id);
      }

      // Initialize other fields based on session data
      setSelectedTrainingType(sessionData?.getSessionById?.trainingType?._id || '');
      setModalSelectedSport(sessionData?.getSessionById?.sportId || '');
      setModalSelectedPosition(sessionData?.getSessionById?.position || '');
    }
  }, [isSessionTypeModalOpen, programsData, sessionData]);

  // Sports options from API
  const sportsOptions =
    sportsData?.getAllSports?.map((sport) => ({
      value: sport?._id || '',
      text: sport?.sportName || '',
    })) || [];

  // Helper function to handle modal program change
  const handleModalProgramChange = (programName: string, programId: string) => {
    // Check balance if freeSessionBalance is 0
    if (sessionBalance?.getCurrentUser?.freeSessionBalance === 0) {
      let balance = 0;
      let errorMessage = '';

      switch (programName.toLowerCase()) {
        case 'fitprime':
          balance = sessionBalance?.getCurrentUser?.fitPrimeBalance ?? 0;
          errorMessage = 'You don\'t have enough FitPrime sessions left. Please buy more to continue.';
          break;
        case 'fitcurve':
          balance = sessionBalance?.getCurrentUser?.fitCurveBalance ?? 0;
          errorMessage = 'You don\'t have enough FitCurve sessions left. Please buy more to continue.';
          break;
        case 'fitvarsity':
          balance = sessionBalance?.getCurrentUser?.fitVarsityBalance ?? 0;
          errorMessage = 'You don\'t have enough FitVarsity sessions left. Please buy more to continue.';
          break;
      }

      if (balance === 0) {
        showToast({
          message: errorMessage,
          variant: 'error'
        });
        return; // Don't proceed with program change
      }
    }

    setModalSelectedProgram(programName);
    setModalSelectedProgramId(programId);

    // Clear training type when switching away from FitPrime
    if (programName !== 'fitprime') {
      setSelectedTrainingType('');
    }

    // Clear sport and position when switching away from FitVarsity
    if (programName !== 'fitvarsity') {
      setModalSelectedSport('');
      setModalSelectedPosition('');
    }
  };

  return (
    <div className='w-full flex justify-center items-center overflow-auto'>
      {!sessionInfoLoading && data?.getLogInfo?.status && userDetails?.role === Role.Trainee ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='flex flex-col gap-5 w-[651px] max-w-[95%] px-3 py-5 md:p-8 bg-[#151525] mt-16 mb-16 rounded-xl'
        >
          <div className='w-full flex justify-between items-center'>
            <h2>Feedback</h2>
            <Button onClick={redirectToHome} id='feedback-close-btn' size='icon' variant='ghost'>
              <X className='w-6 h-6' />
            </Button>
          </div>
          <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4'>
            <Label className='text-xl font-semibold'>Rate the trainer&apos;s expertise:</Label>
            <Controller
              name='expertise'
              control={control}
              render={({ field }) => (
                <ToggleGroup
                  type='single'
                  className='grid grid-cols-2 gap-3'
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <ToggleGroupItem value='Excellent'>Excellent</ToggleGroupItem>
                  <ToggleGroupItem value='Good'>Good</ToggleGroupItem>
                  <ToggleGroupItem value='Average'>Average</ToggleGroupItem>
                  <ToggleGroupItem value='BelowAverage'>Below Average</ToggleGroupItem>
                </ToggleGroup>
              )}
            />
            {errors.expertise && <p className='text-red text-xs'>{errors.expertise.message}</p>}
          </div>
          <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4'>
            <Label className='text-xl font-semibold'>
              Rate the trainer&apos;s communication effectiveness:
            </Label>
            <Controller
              name='communication'
              control={control}
              render={({ field }) => (
                <ToggleGroup
                  type='single'
                  className='grid grid-cols-2 gap-3'
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <ToggleGroupItem value='Excellent'>Excellent</ToggleGroupItem>
                  <ToggleGroupItem value='Good'>Good</ToggleGroupItem>
                  <ToggleGroupItem value='Average'>Average</ToggleGroupItem>
                  <ToggleGroupItem value='BelowAverage'>Below Average</ToggleGroupItem>
                </ToggleGroup>
              )}
            />
            {errors.communication && (
              <p className='text-red text-xs'>{errors.communication.message}</p>
            )}
          </div>
          <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4'>
            <Label className='text-xl font-semibold'>
              How well did the session meet your expectations?
            </Label>
            <Controller
              name='expectations'
              control={control}
              render={({ field }) => (
                <ToggleGroup
                  type='single'
                  className='grid grid-cols-2 gap-3'
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <ToggleGroupItem value='Exceeded Expectaions'>
                    Exceeded Expectations
                  </ToggleGroupItem>
                  <ToggleGroupItem value='Met Expectations'>Met Expectations</ToggleGroupItem>
                  <ToggleGroupItem value='Below Expectations'>Below Expectations</ToggleGroupItem>
                </ToggleGroup>
              )}
            />
            {errors.expectations && (
              <p className='text-red text-xs'>{errors.expectations.message}</p>
            )}
          </div>
          <div className='flex gap-4'>
            <Button
              className='w-full'
              id='feedback-cancel'
              onClick={redirectToHome}
              variant='secondary'
            >
              Skip
            </Button>
            <Button type='submit' loading={loading} className='w-full' id='feedback-continue'>
              Continue
            </Button>
          </div>
        </form>
      ) : !sessionInfoLoading && isTrainer ? (
        <div className='flex flex-col gap-5 w-[651px] max-w-[95%] px-3 py-5 md:p-8 bg-[#151525] mt-16 mb-16 rounded-xl'>
          <div className='flex items-center gap-2'>
            {[2, 3, 4, 5, 6].includes(progress) && (
              <button
                type='button'
                onClick={() => setProgress(progress - 1)}
                className='p-1 rounded hover:text-primary transition-colors'
                aria-label='Back'
              >
                <ArrowLeft className='w-5 h-5' />
              </button>
            )}
            <Progress value={progress * 16.66} max={6} />
          </div>
          {progress === 1 && (
            <div>
              <TrainerFeedbackForm
                traineeId={sessionData?.getSessionById?.traineeId || ''}
                updateProgress={setProgress}
              />
            </div>
          )}
          {progress === 2 && (
            <div className='min-h-[500px] flex justify-between flex-col'>
              <div>
                <h2 className='text-[26px] font-bold text-white mt-6'>Targeted Muscle Group</h2>
                <span className='text-[#A8ADB9] text-sm font-medium mt-1'>
                  You can select multiple items
                </span>
                <ToggleGroup
                  type='multiple'
                  className='grid grid-cols-2 gap-3 mt-6'
                  value={trainerFeedback.muscleGroups}
                  onValueChange={(value) =>
                    setTrainerFeedback((prev) => ({ ...prev, muscleGroups: value }))
                  }
                >
                  <ToggleGroupItem value='Core'>Core</ToggleGroupItem>
                  <ToggleGroupItem value='Shoulder'>Shoulder</ToggleGroupItem>
                  <ToggleGroupItem value='Chest'>Chest</ToggleGroupItem>
                  <ToggleGroupItem value='Back'>Back</ToggleGroupItem>
                  <ToggleGroupItem value='Arms'>Arms</ToggleGroupItem>
                  <ToggleGroupItem value='Legs'>Legs</ToggleGroupItem>
                  <ToggleGroupItem value='Other'>Other</ToggleGroupItem>
                </ToggleGroup>
              </div>
              <Button
                onClick={() => {
                  if (trainerFeedback.muscleGroups.length === 0) {
                    showToast({
                      variant: 'error',
                      message: 'Please select at least one muscle group',
                    });
                    return;
                  } else {
                    setProgress(3);
                  }
                }}
                className='w-full'
                id='feedback-continue'
              >
                Continue
              </Button>
            </div>
          )}
          {progress === 3 && (
            <div className='min-h-[500px] flex justify-between flex-col'>
              <div>
                <h2 className='text-[26px] font-bold text-white mt-6'>Client Performance</h2>
                <ToggleGroup
                  type='single'
                  className='grid grid-cols-1 gap-3 mt-6'
                  value={trainerFeedback.performance}
                  onValueChange={(value) =>
                    setTrainerFeedback((prev) => ({ ...prev, performance: value }))
                  }
                >
                  <ToggleGroupItem value='EXCELLENT'>Excellent</ToggleGroupItem>
                  <ToggleGroupItem value='GOOD'>Good</ToggleGroupItem>
                  <ToggleGroupItem value='NEEDS_IMPROVEMENT'>Needs Improvement</ToggleGroupItem>
                </ToggleGroup>
              </div>
              <Button
                onClick={() => {
                  if (trainerFeedback.performance === '') {
                    showToast({
                      variant: 'error',
                      message: 'Client Performance details are required',
                    });
                    return;
                  }
                  setProgress(4);
                }}
                className='w-full'
                id='feedback-continue'
              >
                Continue
              </Button>
            </div>
          )}
          {progress === 4 && (
            <div className='min-h-[500px] flex justify-between flex-col'>
              <div>
                <h2 className='text-[26px] font-bold text-white mt-6'>Feedback/Notes</h2>
                <CustomTextarea
                  id='feedback-notes'
                  label='Client Feedback / Notes for Future Sessions'
                  placeholder="The session went well! The client demonstrated excellent focus and made great progress with their technique. We'll build on this in upcoming sessions."
                  className='w-full mt-6 min-h-[150px]'
                  value={trainerFeedback.notes}
                  onChange={(e) =>
                    setTrainerFeedback((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
                <CustomTextarea
                  id='feedback-exercise'
                  label='Exercises Performed'
                  placeholder={`Squats
                    Deadlifts
                    Push-Ups
                    Bent-Over Rows
                    Plank
                    `}
                  className='w-full mt-6 min-h-[150px]'
                  value={trainerFeedback.exercise}
                  onChange={(e) =>
                    setTrainerFeedback((prev) => ({ ...prev, exercise: e.target.value }))
                  }
                />
              </div>
              <Button
                onClick={() => {
                  if (trainerFeedback.notes === '') {
                    showToast({
                      variant: 'error',
                      message: 'Feedback/Notes are required',
                    });
                    return;
                  } else {
                    setProgress(5);
                  }
                }}
                className='w-full'
                id='feedback-continue'
              >
                Continue
              </Button>
            </div>
          )}
          {progress === 5 && (
            <div className='min-h-[500px] flex justify-between flex-col'>
              <div>
                <h2 className='text-[26px] font-bold text-white mt-6'>Next Session Scheduling</h2>
                <div className='flex flex-col mt-6 gap-4'>
                  <div className='bg-[#343840] rounded-xl py-2 px-3 flex items-center gap-2'>
                    {(() => {
                      // Find the selected training type from the list
                      const selectedType = trainingTypes?.getAllTrainingType?.find(
                        (type) => type?._id === selectedTrainingType,
                      );
                      const trainingTypeTitle =
                        selectedType?.title || sessionData?.getSessionById?.trainingType?.title;

                      // Get program information - prioritize modal selection over session data
                      const programName = modalSelectedProgram || sessionData?.getSessionById?.programDetails?.programName?.toLowerCase();
                      const programNameLower = programName?.toLowerCase();

                      // Determine icon and display text based on selected program
                      let icon = '';
                      let displayText = '';

                      if (programNameLower === 'fitprime') {
                        // For FitPrime, show only FitPrime(trainingType)
                        icon = selectedType?.icon || sessionData?.getSessionById?.trainingType?.icon || '';
                        displayText = `FitPrime (${trainingTypeTitle})`;
                      } else if (programNameLower === 'fitvarsity') {
                        // For FitVarsity, show FitVarsity icon and name with sport/position if selected
                        icon = FitVarsityIcon;
                        const selectedSport = sportsData?.getAllSports?.find(s => s?._id === modalSelectedSport);
                        const sportName = selectedSport?.sportName || '';
                        const position = modalSelectedPosition || '';

                        if (sportName && position) {
                          displayText = `FitVarsity (${sportName} - ${position})`;
                        } else if (sportName) {
                          displayText = `FitVarsity (${sportName})`;
                        } else {
                          displayText = 'FitVarsity';
                        }
                      } else if (programNameLower === 'fitcurve') {
                        // For FitCurve, show FitCurve icon and name
                        icon = FitCurveIcon;
                        displayText = 'FitCurve';
                      } else {
                        // Fallback to training type
                        icon = selectedType?.icon || sessionData?.getSessionById?.trainingType?.icon || '';
                        displayText = trainingTypeTitle || '';
                      }

                      return (
                        <>
                          <img src={icon} alt='session' className='w-6 h-6' />
                          <span>{displayText}</span>
                        </>
                      );
                    })()}
                    <Button
                      id='change-session-type'
                      variant='ghost'
                      className='text-[#0094FF] ml-auto mr-[12px] hover:text-[#30a2f3]'
                      size='icon'
                      onClick={() => setIsSessionTypeModalOpen(true)}
                    >
                      Change
                    </Button>
                  </div>
                  <div className='flex justify-between items-center gap-2'>
                    <span className='text-[16px] font-medium'>Select Date</span>
                    <div
                      className={`w-full space-y-2 max-w-[250px]${dateError ? ' mb-[3px]' : ''}`}
                    >
                      <DatePicker
                        placeholder='Date'
                        format='MMMM DD, YYYY'
                        classNames='w-full'
                        dateDisabled={{ before: new Date() }}
                        error={dateError}
                        selectedDate={nextSessionDate}
                        onChange={(date) => {
                          setNextSessionDate(date);
                          setDateError(undefined);
                          if (!date) {
                            setNextSessionTime(new Date());
                            return;
                          }
                          const today = dayjs().startOf('day');
                          const selected = dayjs(date).startOf('day');
                          if (selected.isSame(today)) {
                            setNextSessionTime(getNextNearest30Min(new Date()));
                          } else {
                            setNextSessionTime(dayjs(date).hour(9).minute(0).second(0).toDate());
                          }
                        }}
                        clearIcon={false}
                      />
                    </div>
                  </div>
                  <div className='flex justify-between items-center gap-2'>
                    <span className='text-[16px] font-medium'>Select Time</span>
                    <div className='w-full space-y-2 max-w-[250px]'>
                      <TimePicker
                        label='Start Time'
                        value={dayjs(nextSessionTime).format('hh:mm A')}
                        selectedDate={nextSessionTime && new Date(nextSessionTime)}
                        onTimeChange={(newDate) => setNextSessionTime(newDate)}
                      />
                    </div>
                  </div>
                  <div className='flex justify-between items-center gap-2'>
                    <span className='text-[16px] font-medium'>Select Timezone</span>
                    <div className='space-y-2 max-w-[250px]'>
                      <AvailabilityTimezone
                        options={timezonesList}
                        onChange={(value) => {
                          setSelectedTimezone(value);
                        }}
                        value={selectedTimezone}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-4'>
                {nextSessionDate && nextSessionTime && (
                  <div className='flex items-center gap-2 bg-[linear-gradient(154.36deg,rgba(47,47,47,0.44)_11.3%,rgba(47,47,47,0.44)_95.38%)] p-4 rounded-lg'>
                    <Calendar width={24} height={24} className='text-primary mr-1' />
                    <p className='text-sm text-wrap text-gray-300'>
                      Your Session will be scheduled on&nbsp;
                      <span className='text-white'>{dayjs(nextSessionDate).format('MMM DD')}</span>,
                      from&nbsp;
                      <span className='text-white'>{dayjs(nextSessionTime).format('h:mm A')}</span>
                      &nbsp; to&nbsp;
                      <span className='text-white'>
                        {dayjs(nextSessionTime).add(1, 'hour').format('h:mm A')}{' '}
                        {getTimezoneAbbreviation(selectedTimezone)}
                      </span>
                      .
                    </p>
                  </div>
                )}
                <div className='flex flex-row gap-4'>
                  <Button
                    onClick={() => {
                      setSelectedTrainingType('');
                      setNextSessionTime(getNextNearest30Min(new Date()));
                      setNextSessionDate(undefined);
                      setSelectedTimezone('');
                      setShowNextSessionSchedule(false);
                      setProgress(6);
                    }}
                    className='w-full'
                    variant='secondary'
                    id='skip-next-session'
                  >
                    Skip
                  </Button>
                  <Button
                    className='w-full'
                    id='next-session'
                    onClick={() => {
                      if (!nextSessionDate) {
                        setDateError('Please select a date to proceed');
                        return;
                      }
                      setShowNextSessionSchedule(true);
                      setProgress(6);
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}
          {progress === 6 && (
            <div className='min-h-[500px] flex justify-between flex-col'>
              <div>
                <h2 className='text-[26px] font-bold text-white mt-6'>Summary</h2>
                <div className='flex flex-col gap-4 rounded-xl py-4 max-h-[380px] overflow-y-auto'>
                  {trainerFeedback.muscleGroups.length > 0 && (
                    <div
                      style={{
                        background:
                          'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <span className='font-semibold text-sm'>Targeted Muscle Group</span>
                      <span className='text-base'>{trainerFeedback.muscleGroups.join(', ')}</span>
                    </div>
                  )}
                  {trainerFeedback.performance && (
                    <div
                      style={{
                        background:
                          'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <span className='font-semibold text-sm'>Client Performance</span>
                      <span className='text-base'>
                        {formatEnumString(trainerFeedback.performance)}
                      </span>
                    </div>
                  )}
                  {trainerFeedback.notes && (
                    <div
                      style={{
                        background:
                          'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <span className='font-semibold text-sm'>
                        Client Feedback/ Notes for Future Sessions
                      </span>
                      <span className='text-base' style={{ whiteSpace: 'pre-line' }}>
                        {trainerFeedback.notes}
                      </span>
                    </div>
                  )}
                  {trainerFeedback.exercise && (
                    <div
                      style={{
                        background:
                          'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <span className='font-semibold text-sm'>Exercises Performed</span>
                      <span className='text-base whitespace-pre-line'>
                        {trainerFeedback.exercise}
                      </span>
                    </div>
                  )}
                  {showNextSessionSchedule && (
                    <div
                      style={{
                        background:
                          'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <span className='font-semibold text-sm'>Next Session Scheduling</span>
                      <span className='text-base'>
                        {dayjs(nextSessionDate).format('ddd, MMM DD')},{' '}
                        {dayjs(nextSessionTime).format('h:mm A')} (
                        {getTimezoneAbbreviation(selectedTimezone)})
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <Button
                onClick={() => handleSubmitCustomerFeedback()}
                className='w-full '
                id='feedback-continue'
                disabled={createSessionLoading || loading}
              >
                Submit
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Loader />
      )}
      <Modal
        title='Session Type'
        open={isSessionTypeModalOpen}
        onClose={() => setIsSessionTypeModalOpen(false)}
        footer={
          <div className='w-full flex items-center justify-end gap-4 pt-4 px-7 pb-7'>
            <Button id='close-session-type-modal' onClick={() => setIsSessionTypeModalOpen(false)}>
              Close
            </Button>
            <Button
              id='confirm-session-type-modal'
              onClick={() => {
                // Validate required fields based on selected program
                if (modalSelectedProgram === 'fitprime' && !selectedTrainingType) {
                  showToast({ message: 'Please select a training type for FitPrime', variant: 'error' });
                  return;
                }
                if (modalSelectedProgram === 'fitvarsity') {
                  if (!modalSelectedSport) {
                    showToast({ message: 'Please select a sport for FitVarsity', variant: 'error' });
                    return;
                  }
                  if (!modalSelectedPosition) {
                    showToast({ message: 'Please enter a position for FitVarsity', variant: 'error' });
                    return;
                  }
                }
                setIsSessionTypeModalOpen(false);
              }}
            >
              Confirm
            </Button>
          </div>
        }
        className='bg-[#151525] md:max-w-[950px]'
      >
        <div className='bg-[#151525] rounded-xl px-7 py-8 space-y-6 overflow-auto max-h-[calc(100vh-210px)]'>
          {/* Program Selection */}
          <div className='bg-[#00001C] p-[18px] rounded-[20px]'>
            <h4 className='mb-4 font-medium text-base leading-[1.4] text-white'>
              Select Program <span className='text-red-500'>*</span>
            </h4>
            <div className='flex gap-[10px]'>
              {programsLoading
                ? Array(3)
                  .fill(0)
                  .map((_, index) => <Skeleton key={index} className='h-[120px] flex-1' />)
                : programsData?.getAllProgram?.map((program) => {
                  const programName = program?.programName?.toLowerCase() || '';
                  const isSelected = modalSelectedProgram.toLowerCase() === programName;

                  // Map program names to icons
                  const getIcon = (name: string) => {
                    switch (name) {
                      case 'fitvarsity':
                        return FitVarsityIcon;
                      case 'fitcurve':
                        return FitCurveIcon;
                      case 'fitprime':
                        return FitPrimeIcon;
                      default:
                        return FitVarsityIcon; // fallback
                    }
                  };

                  return (
                    <div
                      key={program?._id}
                      className={`flex flex-col justify-center gap-3 cursor-pointer flex-1 px-[14px] py-[18px] rounded-xl ${isSelected
                        ? 'bg-[#181A32] border-2 border-[#0A7AFF]'
                        : 'bg-gradient-to-br from-[#2F2F2F] to-[#2F2F2F]'
                        }`}
                      onClick={() => handleModalProgramChange(programName, program?._id || '')}
                    >
                      <div className='flex items-center gap-3'>
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <img
                            src={getIcon(programName)}
                            alt={program?.programName || ''}
                            className='w-8 h-8'
                          />
                        </div>
                        <span className='text-white text-xl font-medium leading-[1.22] tracking-[0.02em]'>
                          {program?.programName || ''}
                        </span>
                      </div>
                      <p className='text-[#C5C8D0] text-xs font-normal leading-[1.4]'>
                        {program?.description || ''}
                      </p>
                      {(sessionBalance?.getCurrentUser?.freeSessionBalance === 0) && (
                        <p className='text-white text-xs font-medium leading-[1.4] mt-1'>
                          Balance: {
                            programName === 'fitprime' ? sessionBalance?.getCurrentUser?.fitPrimeBalance :
                              programName === 'fitcurve' ? sessionBalance?.getCurrentUser?.fitCurveBalance :
                                programName === 'fitvarsity' ? sessionBalance?.getCurrentUser?.fitVarsityBalance :
                                  0
                          }
                        </p>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Sports Info for FitVarsity */}
          {modalSelectedProgram === 'fitvarsity' && (
            <div className='bg-[#00001C] p-[18px] rounded-[20px]'>
              <h4 className='mb-4 font-medium text-base leading-[1.4] text-white'>
                Sports Info <span className='text-red-500'>*</span>
              </h4>
              <div className='grid gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  {sportsLoading ? (
                    <Skeleton className='h-[56px] w-full' />
                  ) : (
                    <SelectComponent
                      id='modal-select-sport'
                      label='Select Sport *'
                      values={sportsOptions}
                      value={modalSelectedSport}
                      setValue={(value) => setModalSelectedSport(value)}
                      className='border-[#515764] bg-transparent text-white'
                    />
                  )}
                </div>
                <div className='space-y-2'>
                  <CustomInput
                    id='modal-position-input'
                    label='Position *'
                    value={modalSelectedPosition}
                    onChange={(e) => setModalSelectedPosition(e.target.value)}
                    placeholder='e.g., QB, RB, WR, TE, OL'
                    className='border-[#515764] bg-transparent text-white'
                  />
                </div>
              </div>
            </div>
          )}

          {/* Training Type Selection - Show for FitPrime only */}
          {modalSelectedProgram === 'fitprime' && (
            <div className='bg-[#00001C] p-4 rounded-xl'>
              <h4 className='mb-4 font-bold'>
                Select Training Type <span className='text-red-500'>*</span>
              </h4>
              <ToggleGroup
                type='single'
                value={selectedTrainingType}
                onValueChange={(val) => setSelectedTrainingType(val)}
                className='grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3'
              >
                {trainingTypesLoading
                  ? Array(8)
                    .fill(0)
                    .map((_, index) => <Skeleton key={index} className='h-[76px] w-full' />)
                  : trainingTypes?.getAllTrainingType?.map((type) => (
                    <ToggleGroupItem
                      key={type?._id}
                      value={type?._id ?? ''}
                      className='flex gap-1 py-4 px-2 h-auto bg-[#25282E] border-[#25282E] hover:border-[#0A7AFF] hover:bg-[#181A32] hover:text-white data-[state=on]:bg-[#181A32] data-[state=on]:text-white data-[state=on]:border-[#0A7AFF] border-[3px] rounded-xl'
                    >
                      <img
                        src={type?.icon ?? ''}
                        alt={type?.title}
                        width={40}
                        height={40}
                        className='h-10 w-10'
                      />
                      <span className='text-sm font-medium whitespace-break-spaces'>
                        {type?.title}
                      </span>
                    </ToggleGroupItem>
                  ))}
              </ToggleGroup>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Feedback;

function getTimezoneAbbreviation(timezone: string) {
  const abbr = timezones?.filter((tz) => tz.utc.includes(timezone))[0]?.abbr;
  return abbr || timezone;
}
